import { cameraPosesMatch, easeTransitionProgress, interpolateCameraPose, phaseControlsCamera, resolveCameraNavigation, validateCameraPose, validateCameraTravel } from './_pure/cameraNavigation.js';
import { resolveCameraPose } from './_pure/cameraPose.js';
import { shareStructure } from './_pure/structuralSharing.js';
import { derivePhaseFinalFrame, isInstantTransitionPhase, prepareInterpolatedPatch, withFrameCameraPose, } from './_pure/transitionEngine.js';
export function playTransitionPlan(args) {
    let cancelled = false;
    let completed = false;
    let paused = args.paused ?? false;
    let pausedAt = paused ? args.clock.now() : undefined;
    let pausedDuration = 0;
    let frameHandle;
    let delayHandle;
    let pendingFrame;
    let pendingDelay;
    let scheduleVersion = 0;
    let cameraActive = false;
    let previousFrame = args.previousFrame ?? args.base;
    const { clock, camera } = args;
    const activeNow = () => (pausedAt ?? clock.now()) - pausedDuration;
    const unschedule = () => {
        scheduleVersion++;
        if (frameHandle !== undefined)
            clock.cancelFrame(frameHandle);
        if (delayHandle !== undefined)
            clock.cancelDelay(delayHandle);
        frameHandle = undefined;
        delayHandle = undefined;
    };
    const schedulePending = () => {
        if (cancelled || completed || paused)
            return;
        const version = scheduleVersion;
        if (pendingFrame && frameHandle === undefined) {
            frameHandle = clock.requestFrame((timestamp) => {
                if (version !== scheduleVersion || cancelled || paused)
                    return;
                frameHandle = undefined;
                const work = pendingFrame;
                pendingFrame = undefined;
                safely(() => work?.(timestamp - pausedDuration));
            });
        }
        if (pendingDelay && delayHandle === undefined) {
            delayHandle = clock.delay(() => {
                if (version !== scheduleVersion || cancelled || paused)
                    return;
                delayHandle = undefined;
                const work = pendingDelay?.work;
                pendingDelay = undefined;
                safely(() => work?.());
            }, Math.max(0, pendingDelay.due - activeNow()));
        }
    };
    const requestFrame = (work) => {
        pendingFrame = work;
        schedulePending();
    };
    const delay = (work, duration) => {
        pendingDelay = { work, due: activeNow() + duration };
        schedulePending();
    };
    const whenActive = (work) => {
        if (cancelled || completed)
            return;
        if (paused)
            requestFrame(work);
        else
            work();
    };
    const releaseCamera = () => {
        if (cameraActive)
            camera?.setGuided(false);
        cameraActive = false;
    };
    const cancel = () => {
        if (cancelled)
            return;
        cancelled = true;
        unschedule();
        pendingFrame = undefined;
        pendingDelay = undefined;
        releaseCamera();
    };
    const handle = Object.assign(cancel, {
        pause: () => {
            if (cancelled || completed || paused)
                return;
            pausedAt = clock.now();
            paused = true;
            unschedule();
        },
        resume: () => {
            if (cancelled || completed || !paused)
                return;
            pausedDuration += clock.now() - pausedAt;
            pausedAt = undefined;
            paused = false;
            schedulePending();
        },
    });
    const safely = (work) => {
        try {
            work();
        }
        catch (error) {
            cancel();
            throw error;
        }
    };
    const shareFrame = (frame) => {
        // Preserve the frame identity used by camera.stage().
        for (const key of Object.keys(frame)) {
            Object.assign(frame, { [key]: shareStructure(previousFrame[key], frame[key]) });
        }
        return frame;
    };
    const publish = (frame, interpolated = false) => {
        if (!interpolated)
            shareFrame(frame);
        previousFrame = frame;
        camera?.setMode(frame.spaceRenderOptions?.camera?.navigationMode ?? 'free');
        args.onFrame(frame);
    };
    const runPhase = (index, base) => {
        if (cancelled)
            return;
        if (paused) {
            requestFrame(() => runPhase(index, base));
            return;
        }
        const phase = args.phases[index];
        if (!phase) {
            releaseCamera();
            publish(base);
            requestFrame(() => {
                completed = true;
                args.onComplete();
            });
            return;
        }
        const controlsCamera = phaseControlsCamera(phase);
        if (!controlsCamera)
            releaseCamera();
        const begin = () => {
            if (cancelled)
                return;
            let phaseBase = base;
            let startCamera;
            let referenceFrame = camera?.referenceFrame();
            let cameraProgressStart = 0;
            if (controlsCamera) {
                if (!camera)
                    throw new Error('Camera transitions require a camera controller.');
                const pose = camera.read();
                if (pose === null) {
                    requestFrame(begin);
                    return;
                }
                if (!cameraActive)
                    camera.setGuided(true);
                cameraActive = true;
                const captured = camera.read();
                if (captured === null)
                    throw new Error('Camera became unavailable during navigation.');
                startCamera = captured;
                phaseBase = withFrameCameraPose(base, startCamera);
            }
            const { cameraNavigation: _navigation, ...geometryPhase } = phase;
            const interpolated = !isInstantTransitionPhase(phase) && phase.transitionOptions.duration > 0;
            let interpolateFrame;
            let finalFrame;
            if (interpolated) {
                phaseBase = shareFrame({ ...phaseBase });
                interpolateFrame = prepareInterpolatedPatch(phaseBase, phase.patch);
                finalFrame = interpolateFrame(1);
                validateCameraPose(resolveCameraPose(finalFrame.spaceRenderOptions));
            }
            else {
                finalFrame = derivePhaseFinalFrame(phaseBase, geometryPhase);
            }
            const stationState = { ...args.stationState };
            if (controlsCamera && !phase.cameraNavigation)
                delete stationState.current;
            let endCamera = phase.cameraNavigation
                ? resolveCameraNavigation(isInstantTransitionPhase(phase) ? finalFrame : phaseBase, phase.cameraNavigation, startCamera, camera?.viewportAspect?.() ?? 1, stationState)
                : resolveCameraPose(finalFrame.spaceRenderOptions);
            let duration = isInstantTransitionPhase(phase) ? 0 : phase.transitionOptions.duration;
            let routeStart = startCamera;
            let routeProgressStart = 0;
            let routeEasing = isInstantTransitionPhase(phase) ? 'linear' : phase.transitionOptions.easing ?? 'linear';
            let retainTravel = true;
            if (phase.cameraNavigation?.arrival === 'reuse' && startCamera) {
                if (Object.keys(phase.patch).length > 0)
                    throw new Error('Reusable camera arrival phases must not contain geometry patches.');
                const retained = args.travelState?.current;
                if (cameraPosesMatch(startCamera, endCamera))
                    duration = 0;
                else if (retained && retained.referenceFrame === referenceFrame
                    && cameraPosesMatch(retained.destination, endCamera)
                    && cameraPosesMatch(retained.position, startCamera)
                    && JSON.stringify(retained.orbitCenter) === JSON.stringify(phase.cameraNavigation.orbitCenter)) {
                    duration = Math.min(duration, retained.remainingMs);
                    routeStart = retained.start;
                    routeProgressStart = retained.progress01;
                    routeEasing = retained.easing;
                }
            }
            const recordTravel = (position, progress) => {
                if (!retainTravel) {
                    if (args.travelState)
                        delete args.travelState.current;
                    return;
                }
                if (args.travelState && referenceFrame && routeStart)
                    args.travelState.current = {
                        destination: endCamera, referenceFrame, remainingMs: duration * (1 - progress), position,
                        start: routeStart, progress01: routeProgressStart + (1 - routeProgressStart) * progress,
                        easing: routeEasing, orbitCenter: phase.cameraNavigation?.orbitCenter,
                    };
            };
            if (startCamera)
                recordTravel(startCamera, 0);
            if (startCamera && !isInstantTransitionPhase(phase) && phase.transitionOptions.duration > 0) {
                validateCameraTravel(startCamera, endCamera);
            }
            const completePhase = () => {
                if (controlsCamera && args.stationState) {
                    if (stationState.current)
                        args.stationState.current = stationState.current;
                    else
                        delete args.stationState.current;
                }
                args.onPhaseComplete?.(phase);
            };
            let aspect = camera?.viewportAspect?.() ?? 1;
            const withCamera = (frame, progress, rawProgress = progress) => {
                if (!startCamera)
                    return frame;
                const currentReferenceFrame = camera?.referenceFrame();
                const referenceChanged = currentReferenceFrame !== referenceFrame;
                const currentAspect = camera?.viewportAspect?.() ?? 1;
                const framing = phase.cameraNavigation && phase.cameraNavigation.target === undefined;
                if (referenceChanged || (framing && currentAspect !== aspect)) {
                    const captured = camera?.read();
                    if (!captured)
                        throw new Error('Camera is unavailable after changing reference frame.');
                    startCamera = captured;
                    retainTravel = false;
                    routeStart = captured;
                    routeProgressStart = 0;
                    referenceFrame = currentReferenceFrame;
                    cameraProgressStart = progress;
                    aspect = currentAspect;
                    if (phase.cameraNavigation) {
                        if (referenceChanged)
                            delete stationState.current;
                        endCamera = resolveCameraNavigation(phaseBase, phase.cameraNavigation, captured, aspect, stationState);
                        validateCameraTravel(captured, endCamera);
                    }
                }
                const cameraProgress = progress === 1 ? 1 : (progress - cameraProgressStart) / (1 - cameraProgressStart);
                const options = routeProgressStart > 0 && routeStart
                    ? interpolateCameraPose(routeStart, endCamera, easeTransitionProgress(routeProgressStart + (1 - routeProgressStart) * rawProgress, routeEasing), phase.cameraNavigation?.orbitCenter)
                    : interpolateCameraPose(startCamera, endCamera, cameraProgress, phase.cameraNavigation?.orbitCenter);
                const posed = withFrameCameraPose(frame, options);
                if (camera?.stage)
                    camera.stage(posed, options);
                else
                    camera?.apply(options);
                return posed;
            };
            if (isInstantTransitionPhase(phase) || !interpolateFrame || duration === 0) {
                const final = withCamera(finalFrame, 1);
                if (startCamera)
                    recordTravel(resolveCameraPose(final.spaceRenderOptions), 1);
                publish(final);
                whenActive(() => {
                    completePhase();
                    runPhase(index + 1, final);
                });
                return;
            }
            const { easing } = phase.transitionOptions;
            const startedAt = activeNow();
            let previousSample = phaseBase;
            const tick = (timestamp) => {
                if (cancelled)
                    return;
                const progress = Math.max(0, Math.min(1, (timestamp - startedAt) / duration));
                const eased = easeTransitionProgress(progress, easing);
                const sample = interpolateFrame(eased, previousSample);
                previousSample = sample;
                const frame = withCamera(sample, eased, progress);
                if (startCamera)
                    recordTravel(resolveCameraPose(frame.spaceRenderOptions), progress);
                publish(frame, true);
                if (progress === 1) {
                    whenActive(() => {
                        completePhase();
                        runPhase(index + 1, frame);
                    });
                }
                else {
                    requestFrame(tick);
                }
            };
            requestFrame(tick);
        };
        const delayMs = isInstantTransitionPhase(phase) ? phase.transitionOptions.delay ?? 0 : 0;
        if (delayMs > 0) {
            delay(begin, delayMs);
        }
        else {
            begin();
        }
    };
    safely(() => {
        whenActive(() => {
            publish(args.base);
            if (args.phases.length > 0)
                runPhase(0, args.base);
        });
    });
    return handle;
}
